import { onBeforeUnmount, onMounted } from 'vue'

// Runs `callback` on an interval, pausing while the browser tab is hidden and immediately
// re-running (then resuming the interval) when it becomes visible again.
//
// `intervalMs` may be a function so the caller can change the cadence over time (e.g. backoff) -
// call the returned `restart()` after changing what it would return to apply the new value.
export function usePausableInterval(callback: () => void, intervalMs: number | (() => number)) {
  const getIntervalMs = typeof intervalMs === 'function' ? intervalMs : () => intervalMs

  let timerId: ReturnType<typeof setInterval> | null = null

  const stop = () => {
    if (timerId === null) return
    clearInterval(timerId)
    timerId = null
  }

  // Restarts the timer with the current interval value, but only if it's actively running
  // (i.e. the tab is visible) - a no-op while paused.
  const restart = () => {
    if (timerId === null) return
    stop()
    timerId = setInterval(callback, getIntervalMs())
  }

  const start = () => {
    if (timerId !== null) return
    timerId = setInterval(callback, getIntervalMs())
  }

  const handleVisibilityChange = () => {
    if (document.hidden) {
      stop()
    }
    else {
      callback()
      start()
    }
  }

  onMounted(() => {
    if (!document.hidden) {
      start()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
  })

  onBeforeUnmount(() => {
    stop()
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  return { restart }
}
